<template>
  <dx-popup
    :visible="visible"
    :full-screen="isMobile"
    :width="isMobile ? '100%' : 'min(1040px, 94vw)'"
    :height="isMobile ? '100%' : '86vh'"
    :title="title"
    :show-close-button="true"
    :hide-on-outside-click="true"
    :animation="tanpaAnimasi"
    :wrapper-attr="{ class: 'menu-aplikasi' }"
    @shown="onShown"
    @hidden="onHidden"
  >
    <div class="menu" @keydown="onKeydown">
      <div class="menu-cari">
        <i class="fa-solid fa-magnifying-glass menu-cari-ikon" aria-hidden="true"></i>
        <input
          ref="cari"
          v-model="q"
          type="text"
          class="menu-cari-input"
          placeholder="Cari menu atau grup, mis. laporan, stok, jurnal…"
          aria-label="Cari menu"
          autocomplete="off"
          spellcheck="false"
          @keydown.enter.prevent="bukaPertama"
        />
        <span v-if="cari" class="menu-cari-jumlah" aria-live="polite">{{ jumlahHasil }} menu</span>
        <button v-if="q" type="button" class="menu-cari-hapus" aria-label="Hapus pencarian" @click="hapusCari">
          <i class="fa-solid fa-xmark" aria-hidden="true"></i>
        </button>
      </div>

      <nav v-if="!cari && pintas.length" class="menu-pintas" aria-label="Pintasan">
        <router-link v-for="p in pintas" :key="p.path" :to="p.path" class="menu-pintas-item menu-tautan" @click="tutup">
          <i :class="p.icon" aria-hidden="true"></i>{{ p.text }}
        </router-link>
      </nav>

      <div v-if="grup.length" class="menu-gulir">
      <div class="menu-grid">
        <section v-for="g in grup" :key="g.text" class="menu-grup" :class="{ 'grup-cocok': g.cocok }">
          <h3 class="menu-grup-judul">
            <span class="menu-grup-ikon"><i :class="g.icon" aria-hidden="true"></i></span>
            <span class="menu-grup-nama">
              <template v-for="(b, i) in g.bagian" :key="i"><mark v-if="b.hit" class="menu-hit">{{ b.t }}</mark><template v-else>{{ b.t }}</template></template>
            </span>
            <span class="menu-grup-jumlah">{{ g.items.length }}</span>
          </h3>
          <ul class="menu-daftar">
            <li v-for="it in g.items" :key="it.path">
              <router-link
                :to="it.path"
                class="menu-item menu-tautan"
                :class="{ aktif: it.path === $route.path }"
                @click="tutup"
              >
                <template v-for="(b, i) in it.bagian" :key="i"><mark v-if="b.hit" class="menu-hit">{{ b.t }}</mark><template v-else>{{ b.t }}</template></template>
              </router-link>
            </li>
          </ul>
        </section>
      </div>
      </div>

      <div v-else class="menu-kosong">
        <p class="menu-kosong-judul">Tidak ada menu yang cocok dengan “{{ q.trim() }}”.</p>
        <p>Coba kata lain, atau nama grupnya (mis. <button type="button" class="menu-saran" @click="q = 'laporan'">laporan</button>,
          <button type="button" class="menu-saran" @click="q = 'master'">master</button>).</p>
      </div>
    </div>
  </dx-popup>
</template>

<script>
import { DxPopup } from "devextreme-vue/popup";

// Pecah teks menjadi bagian biasa dan bagian yang cocok (tanpa v-html, jadi aman dari markup).
function potong(teks, q) {
  if (!q) return [{ t: teks, hit: false }];
  const kecil = teks.toLowerCase();
  const hasil = [];
  let dari = 0;
  let i = kecil.indexOf(q, dari);
  while (i !== -1) {
    if (i > dari) hasil.push({ t: teks.slice(dari, i), hit: false });
    hasil.push({ t: teks.slice(i, i + q.length), hit: true });
    dari = i + q.length;
    i = kecil.indexOf(q, dari);
  }
  if (dari < teks.length) hasil.push({ t: teks.slice(dari), hit: false });
  return hasil;
}

const tampil = (it) => it.visible !== false;

export default {
  name: "dialog-menu",
  components: { DxPopup },
  props: {
    items: { type: Array, default: () => [] },
    visible: { type: Boolean, default: false },
    title: { type: String, default: "Menu Aplikasi" },
  },
  emits: ["dismiss"],
  data() {
    return {
      q: "",
      // Menu dibuka puluhan kali sehari: tanpa animasi buka/tutup.
      tanpaAnimasi: { show: { type: "fade", duration: 0 }, hide: { type: "fade", duration: 0 } },
    };
  },
  computed: {
    isMobile() {
      return window.innerWidth <= 600;
    },
    cari() {
      return this.q.trim().toLowerCase();
    },
    pintas() {
      return this.items.filter((it) => it.path && !it.items && tampil(it));
    },
    // Grup yang namanya cocok tampil dengan SEMUA anaknya; selain itu hanya anak yang cocok.
    grup() {
      const q = this.cari;
      const hasil = [];
      for (const g of this.items) {
        if (!g.items || !tampil(g)) continue;
        const anak = g.items.filter((c) => c.path && tampil(c));
        if (!anak.length) continue;
        const grupCocok = !!q && g.text.toLowerCase().includes(q);
        const dipilih = !q || grupCocok ? anak : anak.filter((c) => c.text.toLowerCase().includes(q));
        if (!dipilih.length) continue;
        hasil.push({
          text: g.text,
          icon: g.icon,
          cocok: grupCocok,
          bagian: potong(g.text, q),
          items: dipilih.map((c) => ({ path: c.path, text: c.text, bagian: potong(c.text, q) })),
        });
      }
      return hasil;
    },
    jumlahHasil() {
      return this.grup.reduce((n, g) => n + g.items.length, 0);
    },
  },
  methods: {
    hapusCari() {
      this.q = "";
      this.$refs.cari?.focus();
    },
    tutup() {
      this.$emit("dismiss");
    },
    onShown() {
      this.$refs.cari?.focus();
    },
    onHidden() {
      this.q = "";
      this.$emit("dismiss");
    },
    bukaPertama() {
      const g = this.grup[0];
      if (!g) return;
      this.$router.push(g.items[0].path);
      this.tutup();
    },
    // Panah atas/bawah berpindah antara kotak cari dan tautan menu, urut seperti di layar.
    onKeydown(e) {
      if (e.key === "Escape") {
        e.preventDefault();
        this.tutup();
        return;
      }
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      const daftar = [this.$refs.cari, ...this.$el.parentElement.querySelectorAll(".menu .menu-tautan")];
      const sekarang = daftar.indexOf(document.activeElement);
      const ke = e.key === "ArrowDown" ? Math.min(sekarang + 1, daftar.length - 1) : Math.max(sekarang - 1, 0);
      if (daftar[ke]) {
        e.preventDefault();
        daftar[ke].focus();
      }
    },
  },
};
</script>

<style>
.menu-aplikasi .dx-popup-content {
  padding: 0;
}
.menu {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}
.menu-cari {
  position: relative;
  display: flex;
  align-items: center;
  margin: 14px 20px 10px;
}
.menu-cari-ikon {
  position: absolute;
  left: 14px;
  color: var(--text-muted);
  pointer-events: none;
}
.menu-cari-input {
  width: 100%;
  height: 44px;
  padding: 0 128px 0 40px;
  border-radius: 10px;
  border: 1px solid var(--control-line);
  background: var(--surface);
  color: var(--text);
  font: 400 15px/1 var(--font-ui);
  outline: none;
  transition: border-color 140ms ease, box-shadow 140ms ease;
}
.menu-cari-input::placeholder {
  color: var(--text-muted);
}
.menu-cari-input:focus {
  border-color: var(--brand-ink);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--brand-ink) 18%, transparent);
}
.menu-cari-jumlah {
  position: absolute;
  right: 48px;
  font-size: 13px;
  color: var(--text-muted);
}

.menu-pintas {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0 20px 6px;
}
.menu-pintas-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 34px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px solid var(--line);
  background: color-mix(in srgb, var(--brand-paper) 55%, var(--surface));
  color: var(--text);
  font-weight: 600;
  text-decoration: none;
}
.menu-pintas-item i {
  color: var(--brand-ink);
}

.menu-cari-hapus {
  position: absolute;
  right: 6px;
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
}
@media (hover: hover) and (pointer: fine) {
  .menu-cari-hapus:hover {
    background: color-mix(in srgb, var(--text) 8%, transparent);
  }
}
/* Gulir di luar, kolom di dalam: kolom CSS dengan tinggi tetap meluber ke samping. */
.menu-gulir {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 6px 20px 20px;
}
.menu-grid {
  columns: 3 250px;
  column-gap: 16px;
}
.menu-grup {
  break-inside: avoid;
  margin: 0 0 14px;
  padding: 10px 8px 8px;
  border-radius: 12px;
  border: 1px solid var(--line);
  background: var(--surface);
}
.menu-grup.grup-cocok {
  border-color: color-mix(in srgb, var(--brand-sun) 70%, var(--line));
  background: color-mix(in srgb, var(--brand-paper) 45%, var(--surface));
}
.menu-aplikasi .menu-grup .menu-grup-judul {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 6px;
  padding: 0 4px;
  font: 700 14px/1.3 var(--font-ui);
  color: var(--text);
}
.menu-grup-ikon {
  width: 30px;
  height: 30px;
  flex: none;
  border-radius: 9px;
  display: grid;
  place-items: center;
  font-size: 14px;
  background: color-mix(in srgb, var(--brand-ink) 12%, var(--surface));
  color: var(--brand-ink);
}
.dx-swatch-dark .menu-grup-ikon,
.dx-swatch-dark .menu-pintas-item i {
  color: var(--brand-leaf);
}
.menu-grup-nama {
  flex: 1;
}
.menu-grup-jumlah {
  font-weight: 600;
  font-size: 12px;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
}
.menu-daftar {
  list-style: none;
  margin: 0;
  padding: 0;
}
.menu-aplikasi .menu-item {
  display: block;
  padding: 7px 10px 7px 44px;
  border-radius: 8px;
  color: var(--text);
  text-decoration: none;
  line-height: 1.3;
}
.menu-item.aktif {
  font-weight: 700;
  color: var(--brand-ink);
}
.dx-swatch-dark .menu-item.aktif {
  color: var(--brand-leaf);
}
.menu-tautan:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
}
@media (hover: hover) and (pointer: fine) {
  .menu-item:hover,
  .menu-pintas-item:hover {
    background: color-mix(in srgb, var(--brand-sun) 20%, var(--surface));
  }
}
.menu-item:focus-visible {
  background: color-mix(in srgb, var(--brand-sun) 20%, var(--surface));
}
.menu-hit {
  background: color-mix(in srgb, var(--brand-sun) 55%, transparent);
  color: inherit;
  border-radius: 3px;
  padding: 0 1px;
}

.menu-kosong {
  padding: 24px 20px;
  color: var(--text-muted);
}
.menu-kosong-judul {
  margin: 0 0 6px;
  font-weight: 600;
  color: var(--text);
}
.menu-saran {
  border: 0;
  padding: 0;
  background: none;
  color: var(--brand-ink);
  font: inherit;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
}

@media (max-width: 600px) {
  .menu-cari {
    margin: 10px 12px 8px;
  }
  .menu-pintas {
    margin: 0 12px 6px;
  }
  .menu-gulir {
    padding: 4px 12px 16px;
  }
}
</style>
